<template>
  <div id="app">
    <div class="container">
      <IndicatorBox
        subtle
        :indicators="
          mode === 'Country'
            ? {
                'Total commitment': `${totalBudget
                  .toLocaleString('FR', { maximumFractionDigits: 2 })
                  .replace(',', '.')} billion USD`,
                'Largest spending area': largestPolicyAreas
                  .slice(0, 1)
                  .join(', '),
              }
            : {
                'Total spending by all countries': `${totalBudget.toFixed(
                  2
                )} billion USD`,
                'Largest spending on': largestTechs.slice(0, 3).join(', '),
              }
        "
        class="tsr-controls"
      >
        <ControlGroup>
          <Dropdown
            key="modedd"
            label="Show policies by"
            v-model="mode"
            :options="['Country', 'Category']"
            ga-event-category="Sustainable Recovery Tracker"
            ga-event-action="Change mode"
            :ga-event-label="mode"
          />
          <Dropdown
            v-if="mode === 'Country'"
            key="countrydd"
            label="Country/region"
            v-model="country"
            :options="countries"
            ga-event-category="Sustainable Recovery Tracker"
            ga-event-action="Select country"
            :ga-event-label="country"
          />
          <Dropdown
            v-if="mode === 'Category'"
            key="categorydd"
            label="Category"
            v-model="category"
            :options="categories"
            ga-event-category="Sustainable Recovery Tracker"
            ga-event-action="Select category"
            :ga-event-label="category"
          />
        </ControlGroup>
      </IndicatorBox>
      <Tabs>
        <Tab
          title="High-level comparison"
          ga-event-category="Sustainable Recovery Tracker"
          ga-event-action="Select tab"
          ga-event-label="High-level comparison"
        >
          <SpendingTreemap
            :filteredPolicyGroups="filteredPolicyGroups"
            :mode="mode"
            :category="category"
            :categories="categories"
            :country="country"
            :largestBudget="largestBudget"
            @select-country="(selected) => (country = selected)"
          />
        </Tab>
        <Tab
          title="Detailed breakdown"
          ga-event-category="Sustainable Recovery Tracker"
          ga-event-action="Select tab"
          ga-event-label="Detailed breakdown"
        >
          <ControlGroup>
            <p>
              <template
                v-if="mode === 'Category' || country === 'All countries'"
              >
                <small class="tsr-table__country">Advanced economies</small>
                <small class="tsr-table__country tsr-table__country--emerging"
                  >Emerging and developing economies</small
                >
              </template>
              <!-- <small class="tsr-table__category tsr-table__category--private"
                >Spending from SOEs or other public institutions (not included
                in total)</small
              > -->
            </p>
          </ControlGroup>
          <ControlGroup>
            <div class="m-chart-metadata__copyright">
              <p class="f-ui-3">
                Note: Policies represented here are currently in force, or have
                been enacted since Q2 2020 and ended as of today. Other
                sustainable recovery or energy affordability policies can be
                found on our
                <a href="https://www.iea.org/policies"
                  >Policies and Measures Database</a
                >.
              </p>
            </div>
          </ControlGroup>
          <CustomTable class="tsr-table">
            <template #header>
              <TableColumn v-for="column in columns" :key="column.key">
                <Sorter
                  v-if="column.sortable"
                  :sort-by="column.key"
                  :active="column.key === sort.sortBy"
                  :title="column.label"
                  :sort="setSort"
                  :ascending="sort.direction === 'asc'"
                >
                  {{ column.label }}
                </Sorter>
                <template v-else>
                  {{ column.label }}
                </template>
              </TableColumn>
            </template>
            <template #body>
              <TableRow
                v-for="policy in showPolicies"
                :key="policy.key"
                class="tsr-table__row"
                :class="{
                  'tsr-table__row--subtle':
                    mode === 'Category' &&
                    policy.category !== 'Multiple' &&
                    policy.category !== category,
                }"
              >
                <template v-for="column in columns">
                  <TableCell
                    :key="`${column.key}${policy.key}`"
                    v-if="
                      !['relevantBudget', 'title', 'tech'].includes(column.key)
                    "
                    :item="policy"
                    :column="column.key"
                    :align="column.align"
                    :formatter="formatters[column.key]"
                    :class="{
                      'tsr-table__country': column.key === 'country',
                      'tsr-table__category': column.key === 'category',
                      'tsr-table__category--filtered':
                        column.key === 'category' &&
                        (!policy.category === category ||
                          policy.categories.includes(category)),
                      'tsr-table__country--emerging':
                        column.key === 'country' &&
                        emerging.includes(policy.country),
                      [`tsr-table__category--${policy.category
                        .toLowerCase()
                        .split(' ')
                        .join('-')}`]: column.key === 'category',
                    }"
                  />
                  <td
                    v-else-if="column.key === 'title'"
                    :key="`${column.key}${policy.key}`"
                    class="m-datatable-table-cell f-ui-2"
                    :style="{ textAlign: 'left' }"
                  >
                    <template v-if="policy.isGroup">
                      <a
                        :href="getPolicyLink(policy)"
                        target="_blank"
                        title="View this policy in the IEA Policies and Measures Database"
                      >
                        {{ policy.title }}
                      </a>
                      <div class="tsr-table__parent-policies">
                        <small v-if="policy.parentIDs.length > 0">
                          Part of
                          <template v-for="(parent, i) in policy.parentTitles">
                            <span v-if="i > 0" :key="`${parent}and`">{{
                              i === policy.parentTitles.length - 1
                                ? " and "
                                : ", "
                            }}</span>
                            <a
                              :href="
                                getPolicyLink({
                                  id: policy.parentIDs[i],
                                  title: parent,
                                })
                              "
                              target="_blank"
                              title="View this policy in the IEA Policies and Measures Database"
                              :key="parent"
                            >
                              {{ parent }}
                            </a>
                          </template>
                        </small>
                      </div>
                    </template>
                  </td>
                  <td
                    v-else-if="column.key === 'tech'"
                    :key="`${column.key}${policy.key}`"
                    class="m-datatable-table-cell f-ui-2"
                    :style="{ textAlign: 'left', whiteSpace: 'nowrap' }"
                  >
                    {{ policy.tech }}{{ "\xa0"
                    }}<span
                      v-if="policy.isGroup && policy.policies.length > 1"
                      class="tsr-table__show-budget"
                      @click="togglePolicyGroup(policy.index)"
                    >
                      <Icon
                        :icon="policy.open ? 'minus' : 'plus'"
                        :title="`${
                          policy.open ? 'Hide' : 'Show'
                        } budget lines`"
                    /></span>
                  </td>
                  <td
                    v-else
                    :key="`${column.key}${policy.key}`"
                    class="m-datatable-table-cell f-ui-2"
                  >
                    <BudgetBar
                      :amount="
                        policy.policies && mode == 'Category'
                          ? policy.policies.reduce(
                              (total, subPolicy) =>
                                subPolicy.category === category
                                  ? total + subPolicy.budgetUSD
                                  : total,
                              0
                            )
                          : policy.budgetUSD
                      "
                      :otherAmount="policy.otherBudget"
                      :max="largestBudget"
                      :total="totalBudget"
                    />
                  </td>
                </template>
              </TableRow>
            </template>
          </CustomTable>
        </Tab>
      </Tabs>
    </div>
  </div>
</template>

<script>
import {
  ControlGroup,
  Dropdown,
  CustomTable,
  TableColumn,
  Sorter,
  Icon,
  TableRow,
  TableCell,
  IndicatorBox,
  Tab,
  Tabs
} from "@international-energy-agency/vue-ui";
import BudgetBar from "./components/BudgetBar.vue";
import SpendingTreemap from "./components/SpendingTreemap.vue";
import {
  policies,
  policyGroups,
  totalByPolicyId,
  baseColumns,
  emerging
} from "./data/data";
import techsByCategory from "./data/techsByCategory";

export default {
  name: "App",
  components: {
    ControlGroup,
    Dropdown,
    CustomTable,
    TableColumn,
    Sorter,
    Icon,
    TableRow,
    TableCell,
    BudgetBar,
    SpendingTreemap,
    IndicatorBox,
    Tab,
    Tabs
  },
  props: {
    test: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const yearFormatter = value => `${value}`;
    const countries = policies
      .reduce(
        (countries, policy) =>
          countries.includes(policy.country)
            ? countries
            : [...countries, policy.country],
        ["All countries"]
      )
      .sort((a, b) => {
        if (a === "All countries") return -1;
        if (b === "All countries") return 1;
        return a < b ? -1 : a > b ? 1 : 0;
      });
    const categories = Object.keys(techsByCategory)
      .filter(
        category =>
          category !== "General" &&
          policies.some(policy => policy.category === category)
      )
      .sort();
    categories.push("General");
    /*policies
      .reduce(
        (categories, policy) =>
          categories.includes(policy.category)
            ? categories
            : [...categories, policy.category],
        []
      )
      .sort();*/
    return {
      mode: "Country",
      countries,
      country: countries[0],
      categories,
      category: categories[0],
      policies,
      policyGroups,
      totalByPolicyId,
      sort: {
        sortBy: "relevantBudget",
        direction: "dsc"
      },
      formatters: {
        startYear: yearFormatter,
        endYear: yearFormatter
      },
      emerging,
      notes: null,
      totalBudget: 0,
      largestBudget: 0,
      totalByCategory: {},
      totalByTech: {}
    };
  },
  created() {
    this.setTotals();
  },
  watch: {
    filteredPolicyGroups: {
      handler: "setTotals"
    }
  },
  methods: {
    setSort(sortBy, direction) {
      this.sort = { sortBy, direction };
    },
    setTotals() {
      const { filteredPolicyGroups, mode, category } = this;
      this.totalBudget = 0;
      this.largestBudget = 0;
      this.totalByCategory = {};
      this.totalByTech = {};
      filteredPolicyGroups.forEach(policyGroup => {
        // used only for scaling bars
        const relevantBudget =
          mode === "Category"
            ? policyGroup.policies.reduce(
                (total, policy) =>
                  policy.category === category
                    ? total + policy.budgetUSD
                    : total,
                0
              )
            : policyGroup.budgetUSD;
        this.largestBudget =
          relevantBudget > this.largestBudget
            ? relevantBudget
            : this.largestBudget;
        policyGroup.relevantBudget = relevantBudget; // HACK
        policyGroup.policies.forEach(policy => {
          // policy.relevantBudget = mode !== "Category" || category === policy.category ? policy.budgetUSD : 0; // HACK
          if (!this.totalByCategory[policy.category])
            this.totalByCategory[policy.category] = 0;
          this.totalByCategory[policy.category] += policy.budgetUSD;
          // only include if the policy matches the category
          if (mode !== "Category" || category === policy.category) {
            if (!this.totalByTech[policy.tech])
              this.totalByTech[policy.tech] = 0;
            this.totalBudget += policy.budgetUSD;
            this.totalByTech[policy.tech] += policy.budgetUSD;
          }
        });
      });
    },
    togglePolicyGroup(i) {
      this.policyGroups[i].open = !this.policyGroups[i].open;
    },
    getPolicyLink(policy) {
      const { test } = this;
      let title = policy.title
        .replace(/@/, "-at-")
        // replace the º symbol with o
        .replace(/º/g, "o")
        //.replace(new RegExp(`[^-${pL}\d+\s]+`, 'g'), '-') /* eslint-disable-line no-useless-escape */
        .replace(/[^-a-zA-Z\d\s]+/g, "") // remove non-alphanumeric characters
        .replace(/[-\s]+/g, "-") // replace whitespace with dashes
        .toLowerCase();
      return `https://${test ? "test" : "www"}.iea.org/policies/${
        policy.id
      }-${title}`;
    }
  },
  computed: {
    columns() {
      return this.mode === "Country" && this.country !== "All countries"
        ? [
            {
              key: "category",
              label: "Category",
              sortable: true,
              align: "left"
            },
            ...baseColumns
          ]
        : [
            {
              key: "country",
              label: "Country/region",
              sortable: true,
              align: "left"
            },
            ...baseColumns
          ];
    },
    filteredPolicyGroups() {
      const { mode, country, category } = this;
      return mode === "Country"
        ? country === "All countries"
          ? this.policyGroups
          : this.policyGroups.filter(group => group.country === country)
        : this.policyGroups.filter(group =>
            group.categories.includes(category)
          );
    },
    showPolicies() {
      const { direction, sortBy } = this.sort;
      return this.filteredPolicyGroups
        .slice(0)
        .sort((a, b) => {
          const aIsBigger =
            sortBy === "budgetUSD"
              ? (a.budgetUSD || a.otherBudget) > (b.budgetUSD || b.otherBudget)
              : a[sortBy] > b[sortBy];
          if (direction === "asc") {
            return aIsBigger ? 1 : -1;
          }
          return aIsBigger ? -1 : 1;
        })
        .reduce(
          (policies, group) =>
            group.open
              ? [
                  ...policies,
                  group,
                  ...group.policies.sort((a, b) => {
                    const aIsBigger = a[sortBy] > b[sortBy];
                    if (direction === "asc") {
                      return aIsBigger ? 1 : -1;
                    }
                    return aIsBigger ? -1 : 1;
                  })
                ]
              : [...policies, group],
          []
        );
    },
    largestPolicyAreas() {
      const { totalByCategory } = this;
      return Object.keys(totalByCategory).sort(
        (a, b) => totalByCategory[b] - totalByCategory[a]
      );
    },
    largestTechs() {
      const { totalByTech } = this;
      return Object.keys(totalByTech).sort(
        (a, b) => totalByTech[b] - totalByTech[a]
      );
    }
  }
};
</script>

<style>
.f-rte small a:not(.a-tag):not(.a-link):not(.a-link--accent) {
  box-shadow: inset 0 0 0 0 #fff, inset 0 -1px 0 0 #6f6f6f;
}
.m-data-table
  tr.m-data-table__row:hover
  a:not(.a-tag):not(.a-link):not(.a-link--accent) {
  box-shadow: inset 0 0 0 0 #fff, inset 0 -1px 0 0 #fff;
}
.m-data-table tr.m-data-table__row:hover small {
  color: #e4e4e4;
}
/* solve bug with ordering of dropdown */
.tsr-controls {
  z-index: 2;
  position: relative;
}
.tsr-table {
  z-index: 1;
}
.tsr-table__show-budget {
  cursor: pointer;
}
.tsr-table__row td {
  vertical-align: middle;
}
.tsr-table__country,
.tsr-table__category {
  border-left: 8px solid #04f;
  padding: 0px 8px;
}
.tsr-table__country--emerging {
  border-color: #af6ab1;
}
.tsr-table__category--private {
  border-color: #80a2ff;
}
.tsr-table__category--energy-efficient-buildings-and-industry {
  border-color: #49d3ff; /* color 0 */
}
.tsr-table__category--low-carbon-electricity {
  border-color: #68f394; /* color 2 */
}
.tsr-table__category--electricity-networks {
  border-color: #fef8a0; /* color 4 */
}
.tsr-table__category--fuels-and-technology-innovation {
  border-color: #ffb743; /* color 5 */
}
.tsr-table__category--low-carbon-and-efficient-transport {
  border-color: #b187ef; /* color 7 */
}
.tsr-table__category--other {
  border-color: #b1b1b1; /* color 9 */
}
.tsr-table__category--access {
  border-color: #b187ef;
}
.tsr-table__category--bioenergy {
  border-color: #68f394;
}
.tsr-table__category--cars-efficiency {
  border-color: #ffb743;
}
.tsr-table__category--fossil {
  border-color: #e8dbfa;
}
.tsr-table__category--energy-access-and-just-transitions {
  border-color: #ff754b;
}
.tsr-table__category--mass-transit {
  border-color: #fef8a0;
}
.highcharts-treemap-series .highcharts-point {
  stroke-width: 0.25px;
  stroke: #000;
}
.highcharts-treemap-series .highcharts-level-group-1 .highcharts-point {
  stroke-width: 2px;
}
.tsr-table__parent-policies {
  display: flex;
}
.tsr-table__row--subtle td {
  color: #a5a5a5;
}
</style>
